/**
 * @kgc/rental-pricing — Hungarian calendar primitives
 * ADR-108 P2: single source of truth for Hungarian holidays + calendar-day math.
 *
 * QA-88 (2026-08-21, P0 MONEY): a naptári nap a MAGYAR naptáron dől el, nem UTC-n.
 * A korábbi „minden UTC" konvenció a TZ-driftet akarta elkerülni, de a magyar naptári
 * nap nem UTC-nap: a helyi éjfél nyáron 22:00Z, télen 23:00Z az ELŐZŐ napon. Ezért a
 * varázsló (helyi nap) és a BE (UTC nap) két különböző számot adott ugyanarra a bérlésre
 * (mérve: 12 700 Ft ajánlat vs. 6 350 Ft létrejött bérlés, BER2026-01077).
 *
 * A dátumrész-kinyerés `Intl` zóna-adatból megy, NEM a process TZ-jéből:
 *  - független a `TZ` env-től és a rendszer `tzdata`-jától (a konténerben nincs is),
 *  - ezért a CI (UTC), a futó dev4 (UTC) és a fejlesztői gép (Budapest) UGYANAZT adja.
 * A belső reprezentáció változatlanul UTC-éjfélre normalizált `Date` = „naptári nap".
 *
 * Previously duplicated in:
 *   - packages/rental/rental-core/src/services/rental-pricing-calculator.ts
 *   - apps/kgc-web/src/pages/rental/wizard-store.ts
 */

/**
 * A bérlés kanonikus naptára (ADR-037). Minden naptári-nap döntés ezen a zónán dől el.
 */
export const RENTAL_TIME_ZONE = 'Europe/Budapest';

const ZONED_DAY_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function getZonedDayFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = ZONED_DAY_FORMATTERS.get(timeZone);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    ZONED_DAY_FORMATTERS.set(timeZone, formatter);
  }
  return formatter;
}

/**
 * Egy időpont naptári napja a megadott zónában, UTC-éjfélre normalizálva.
 *
 * A visszaadott `Date` NEM időpont, hanem „naptári nap" reprezentáció — a modul
 * minden kulcsa és ciklusa ebben az alakban dolgozik. A zóna-feloldás `Intl`-ből
 * jön, tehát a process `TZ`-je és a rendszer `tzdata` jelenléte NEM befolyásolja.
 */
export function toZonedCalendarDay(date: Date, timeZone: string = RENTAL_TIME_ZONE): Date {
  const parts = getZonedDayFormatter(timeZone).formatToParts(date);
  let year = 0;
  let month = 0;
  let day = 0;
  for (const part of parts) {
    if (part.type === 'year') year = Number(part.value);
    else if (part.type === 'month') month = Number(part.value);
    else if (part.type === 'day') day = Number(part.value);
  }
  if (year === 0 || month === 0 || day === 0) {
    throw new Error(`Nem sikerült a naptári nap kinyerése (${timeZone})`);
  }
  return new Date(Date.UTC(year, month - 1, day));
}

const ZONED_OFFSET_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function getZonedOffsetFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = ZONED_OFFSET_FORMATTERS.get(timeZone);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    ZONED_OFFSET_FORMATTERS.set(timeZone, formatter);
  }
  return formatter;
}

/** A zóna UTC-től vett eltolása ms-ban, az adott PILLANATBAN (DST-tudatos). */
function getZoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = getZonedOffsetFormatter(timeZone).formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find(p => p.type === type);
    return part === undefined ? 0 : Number(part.value);
  };
  // A 24:00 alak néhány ICU-verzióban 0 helyett 24-et ad éjfélre.
  const hour = get('hour') % 24;
  const asIfUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    hour,
    get('minute'),
    get('second')
  );
  return asIfUtc - instant.getTime();
}

/**
 * Helyi FALI ÓRA → UTC pillanat a megadott zónában (QA-88 ③).
 *
 * A `HH:MM` alakú bemenet a magyar fali órát jelenti; a korábbi `setUTCHours` ezt
 * UTC-órának vette, és a tárolt `start_date` így 2 órával elcsúszott (14:54 → 16:54).
 *
 * A DST-határ miatt két iteráció kell: az első becslés offsetje még a rossz oldalról
 * jöhet. ⚠️ A nem létező helyi órák (tavaszi óraátállítás 02:00–03:00) a következő
 * létező pillanatra esnek — ez a `Date` szemantikája, nem külön kezelt eset.
 */
export function zonedWallClockToUtc(
  calendarDay: Date,
  hours: number,
  minutes: number,
  timeZone: string = RENTAL_TIME_ZONE
): Date {
  const naive = Date.UTC(
    calendarDay.getUTCFullYear(),
    calendarDay.getUTCMonth(),
    calendarDay.getUTCDate(),
    hours,
    minutes,
    0,
    0
  );
  const firstGuess = naive - getZoneOffsetMs(new Date(naive), timeZone);
  const refined = naive - getZoneOffsetMs(new Date(firstGuess), timeZone);
  return new Date(refined);
}

/** Fixed Hungarian national holidays (month/day) */
export interface FixedHoliday {
  month: number;
  day: number;
  name: string;
}

export const FIXED_HUNGARIAN_HOLIDAYS: readonly FixedHoliday[] = Object.freeze([
  { month: 1, day: 1, name: 'Újév' },
  { month: 3, day: 15, name: 'Nemzeti ünnep' },
  { month: 5, day: 1, name: 'Munka ünnepe' },
  { month: 8, day: 20, name: 'Államalapítás' },
  { month: 10, day: 23, name: '1956-os forradalom' },
  { month: 11, day: 1, name: 'Mindenszentek' },
  { month: 12, day: 25, name: 'Karácsony' },
  { month: 12, day: 26, name: 'Karácsony 2. nap' },
]);

/**
 * Anonymous Gregorian algorithm for Easter Sunday (UTC).
 * Returns the UTC date of Easter Sunday for the given year.
 */
export function getEasterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * Get all Hungarian holidays (fixed + Easter Monday + Whit Monday) for a given year.
 */
export function getHungarianHolidays(year: number): Date[] {
  const holidays: Date[] = [];

  for (const h of FIXED_HUNGARIAN_HOLIDAYS) {
    holidays.push(new Date(Date.UTC(year, h.month - 1, h.day)));
  }

  const easter = getEasterSunday(year);
  const easterMonday = new Date(easter);
  easterMonday.setUTCDate(easter.getUTCDate() + 1);
  holidays.push(easterMonday);

  const whitMonday = new Date(easter);
  whitMonday.setUTCDate(easter.getUTCDate() + 50);
  holidays.push(whitMonday);

  return holidays;
}

/**
 * Build a Set of holiday date keys (YYYY-M-D, 0-indexed month) for fast lookup.
 * Covers all years in [startDate, endDate].
 */
export function buildHolidaySet(startDate: Date, endDate: Date): Set<string> {
  const set = new Set<string>();
  // QA-88: az évhatár is a zónás naptári napból jön (szilveszter 23:00Z már a köv. év)
  const startYear = toZonedCalendarDay(startDate).getUTCFullYear();
  const endYear = toZonedCalendarDay(endDate).getUTCFullYear();

  for (let year = startYear; year <= endYear; year++) {
    for (const h of getHungarianHolidays(year)) {
      set.add(`${h.getUTCFullYear()}-${h.getUTCMonth()}-${h.getUTCDate()}`);
    }
  }

  return set;
}

/**
 * Build a Map of holiday date keys → display names (YYYY-M-D, 0-indexed month).
 */
export function buildHolidayNameMap(startDate: Date, endDate: Date): Map<string, string> {
  const map = new Map<string, string>();
  // QA-88: az évhatár is a zónás naptári napból jön
  const startYear = toZonedCalendarDay(startDate).getUTCFullYear();
  const endYear = toZonedCalendarDay(endDate).getUTCFullYear();

  for (let year = startYear; year <= endYear; year++) {
    for (const h of FIXED_HUNGARIAN_HOLIDAYS) {
      const d = new Date(Date.UTC(year, h.month - 1, h.day));
      map.set(`${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`, h.name);
    }
    const easter = getEasterSunday(year);
    const easterMonday = new Date(easter);
    easterMonday.setUTCDate(easter.getUTCDate() + 1);
    map.set(
      `${easterMonday.getUTCFullYear()}-${easterMonday.getUTCMonth()}-${easterMonday.getUTCDate()}`,
      'Húsvét hétfő'
    );
    const whitMonday = new Date(easter);
    whitMonday.setUTCDate(easter.getUTCDate() + 50);
    map.set(
      `${whitMonday.getUTCFullYear()}-${whitMonday.getUTCMonth()}-${whitMonday.getUTCDate()}`,
      'Pünkösd hétfő'
    );
  }

  return map;
}

/**
 * Check if a UTC date is a Hungarian holiday (given a precomputed holiday set).
 */
export function isHungarianHoliday(date: Date, holidaySet: Set<string>): boolean {
  const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`;
  return holidaySet.has(key);
}

/**
 * Naptári napok két időpont között, inkluzívan — a MAGYAR naptáron (QA-88).
 * @throws Error if endDate < startDate
 */
export function calculateCalendarDays(startDate: Date, endDate: Date): number {
  const start = toZonedCalendarDay(startDate).getTime();
  const end = toZonedCalendarDay(endDate).getTime();
  if (end < start) {
    throw new Error('endDate nem lehet korábbi mint startDate');
  }
  const msPerDay = 86_400_000;
  return Math.round((end - start) / msPerDay) + 1;
}
